import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { AuthProvider, useAuthContext } from "@/contexts/AuthContext";
import {
  HouseholdProvider,
  useHouseholdContext,
} from "@/contexts/HouseholdContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { PaywallProvider } from "@/contexts/PaywallContext";
import { I18nProvider } from "@/contexts/I18nContext";
import { Header } from "@/components/layout/Header";
import { BottomNav } from "@/components/layout/BottomNav";
import { Sidebar } from "@/components/layout/Sidebar";
import { BillingBanners } from "@/components/features/BillingBanners";
import { CoveredByBanner } from "@/components/features/CoveredByBanner";
import { LocalePromptBanner } from "@/components/i18n/LocalePromptBanner";
import { WelcomeOverlay } from "@/components/features/WelcomeOverlay";

function Spinner() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div
        className="w-8 h-8 border-3 border-t-transparent rounded-full animate-spin"
        style={{ borderColor: "var(--accent-color, #00C7BE)", borderTopColor: "transparent" }}
      />
    </div>
  );
}

function AuthGate({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuthContext();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate("/login");
    }
  }, [user, loading, navigate]);

  if (loading) return <Spinner />;
  if (!user) return null;

  return (
    <HouseholdProvider>
      <I18nProvider>
        <ThemeProvider>
          <PaywallProvider>
            <HouseholdGate>{children}</HouseholdGate>
          </PaywallProvider>
        </ThemeProvider>
      </I18nProvider>
    </HouseholdProvider>
  );
}

/**
 * Redirects signed-in users with zero households to the exclusive
 * `/onboarding` gate per [[specs/new-user-onboarding-and-join-flow]]. The
 * loading guard from PR #76 commit `ee39d0e` lives inside HouseholdContext —
 * `loading` stays true while `profile === null`, so we never see a spurious
 * `hasNoHouseholds === true` on cold mount.
 */
function HouseholdGate({ children }: { children: React.ReactNode }) {
  const { profile, loading } = useHouseholdContext();
  const navigate = useNavigate();
  const [welcomeDismissed, setWelcomeDismissed] = useState(false);

  const hasNoHouseholds = !loading && !profile?.households?.length;

  useEffect(() => {
    if (hasNoHouseholds) {
      navigate("/onboarding", { replace: true });
    }
  }, [hasNoHouseholds, navigate]);

  if (loading || hasNoHouseholds) return <Spinner />;

  // Show the 4-slide welcome overlay once per member, after they have a
  // household. Mirrors iOS WelcomeOverlayView gating on
  // `welcome_overlay_shown` and Android `welcome_overlay_shown`.
  const shouldShowWelcome =
    !welcomeDismissed && profile?.welcome_overlay_shown !== true;

  return (
    <div className="flex min-h-screen bg-gray-50/80 dark:bg-gray-950 text-gray-900 dark:text-gray-100">
      <Sidebar />
      <div className="flex-1 flex flex-col min-h-screen">
        <div className="sm:hidden">
          <Header />
        </div>
        <BillingBanners />
        <CoveredByBanner />
        <main className="flex-1 pb-20 sm:pb-0">{children}</main>
        <BottomNav />
      </div>
      <LocalePromptBanner />
      {shouldShowWelcome && (
        <WelcomeOverlay onDismiss={() => setWelcomeDismissed(true)} />
      )}
    </div>
  );
}

export function AppLayout() {
  return (
    <AuthProvider>
      <AuthGate>
        <Outlet />
      </AuthGate>
    </AuthProvider>
  );
}
